/**
 * Coups de pouce — Attendu 1081 « Positionner sur une échelle de probabilité ».
 *
 * Entrée PARTAGÉE par les deux formats de positionnement (position_qcm_unique,
 * echelle_classer) : même geste — comparer aux repères 0 ; 0,5 ; 1 et réserver
 * les extrémités (méthode de la fiche n°43). L'exemple (revue 2026-09-01) est
 * HORS du vivier du gen : dé qui donne 8, urne de boules BLEUES et JAUNES,
 * 12 fois de suite face. L'étape 4 rejoue la question e. de la fiche (« très
 * improbable n'est pas impossible »). SVG pas à pas piloté par les flèches de
 * la sheet (data-etapes / data-caption, mécanisme 2026-08-21).
 *
 * comparer_qcm_unique : entrée dédiée (comparaison sans calcul, deux urnes).
 * extremites_tableau_vf : PAS d'entrée (pur rappel, doctrine gen_640) —
 * chaque ligne EST une connaissance sur l'échelle, toute aide la donnerait.
 */

const SVG_TROIS_REPERES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="4"
     aria-label="Positionner sur l'échelle de probabilité, en 4 étapes à faire défiler avec les flèches. L'échelle va de 0, impossible, à 1, certain, avec 0,5 au milieu : une chance sur deux. Obtenir 8 avec un dé à six faces : aucune face ne le porte, c'est impossible, le point se pose sur 0. Tirer la boule bleue dans une urne qui contient 1 boule bleue et 1 boule jaune : une chance sur deux, le point se pose sur 0,5. Obtenir 12 fois de suite face : c'est possible mais très improbable, le point se pose tout près de 0 sans le toucher, il reste un petit écart. Les extrémités sont réservées">
  <style>
    @keyframes cp1081-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1081-pop { opacity: 0; animation: cp1081-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Les trois repères : 0, 0,5 et 1</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Obtenir 8 avec un dé ? Impossible : sur 0</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Une chance sur deux : sur 0,5</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="4" font-family="system-ui,sans-serif">Très improbable n'est pas impossible : près de 0</text>

  <!-- ── L'échelle et ses trois repères (étape 1) ────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <line x1="30" y1="110" x2="330" y2="110" stroke="#334155" stroke-width="2.5"/>
    <line x1="30" y1="102" x2="30" y2="118" stroke="#334155" stroke-width="2"/>
    <line x1="180" y1="102" x2="180" y2="118" stroke="#334155" stroke-width="2"/>
    <line x1="330" y1="102" x2="330" y2="118" stroke="#334155" stroke-width="2"/>
    <text x="30" y="136" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">0</text>
    <text x="180" y="136" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">0,5</text>
    <text x="330" y="136" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">1</text>
    <text x="30" y="72" font-size="12" font-weight="600" fill="#475569" text-anchor="start">impossible</text>
    <text x="180" y="72" font-size="12" font-weight="600" fill="#475569" text-anchor="middle">une chance sur deux</text>
    <text x="330" y="72" font-size="12" font-weight="600" fill="#475569" text-anchor="end">certain</text>
  </g>

  <!-- ── L'impossible se pose SUR 0 (étape 2) ────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1081-pop">
      <circle cx="30" cy="110" r="5.5" fill="#1E3A8A" stroke="#ffffff" stroke-width="1.5"/>
      <text x="8" y="164" font-size="12.5" font-weight="700" fill="#1E3A8A">Obtenir 8 avec un dé : impossible, point sur 0</text>
    </g>
  </g>

  <!-- ── Une chance sur deux se pose SUR 0,5 (étape 3) ───────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1081-pop">
      <circle cx="180" cy="110" r="5.5" fill="#1D9E75" stroke="#ffffff" stroke-width="1.5"/>
      <text x="8" y="186" font-size="12.5" font-weight="700" fill="#1D9E75">La boule bleue (1 bleue, 1 jaune) : point sur 0,5</text>
    </g>
  </g>

  <!-- ── Le piège : très improbable, PRÈS de 0 (étape 4) ─────────────── -->
  <g data-etape="4" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1081-pop">
      <circle cx="46" cy="110" r="5.5" fill="#B45309" stroke="#ffffff" stroke-width="1.5"/>
      <line x1="30" y1="96" x2="30" y2="104" stroke="#B45309" stroke-width="1.6"/>
      <line x1="46" y1="96" x2="46" y2="104" stroke="#B45309" stroke-width="1.6"/>
      <line x1="30" y1="98" x2="46" y2="98" stroke="#B45309" stroke-width="1.6"/>
      <line x1="46" y1="96" x2="60" y2="86" stroke="#B45309" stroke-width="1.2"/>
      <text x="63" y="88" font-size="12" font-weight="700" fill="#B45309">il reste un petit écart</text>
      <text x="8" y="208" font-size="12.5" font-weight="700" fill="#B45309">12 fois de suite face : possible, donc pas sur 0 !</text>
      <rect x="20" y="218" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="235" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Les extrémités sont réservées.</text>
    </g>
  </g>

  <text x="180" y="249" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

/*
 * comparer_qcm_unique : deux urnes hors vivier (bleues/jaunes) — presque
 * identiques, positions très différentes. Le geste : placer en pensée,
 * le plus à droite gagne.
 */
const SVG_COMPARER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 236" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Comparer deux évènements, en 3 étapes à faire défiler avec les flèches. Évènement A : tirer la boule bleue dans une urne qui contient 1 boule bleue et 1 boule jaune. Évènement B : tirer une boule bleue dans une urne qui ne contient que des boules bleues. Je place A en pensée : une chance sur deux, point sur 0,5. Je place B : c'est certain, point sur 1, tout à droite. B est le plus probable : le plus à droite est le plus probable">
  <style>
    @keyframes cp1081b-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1081b-pop { opacity: 0; animation: cp1081b-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Deux urnes : 1 bleue et 1 jaune, ou toutes bleues ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="0" data-caption="2" font-family="system-ui,sans-serif">A : une chance sur deux, point sur 0,5</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">B : certain, point sur 1. B est le plus probable</text>

  <!-- ── Les deux évènements et l'échelle (étape 1) ──────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="46" font-size="12.5" font-weight="700" fill="#1E3A8A">A. La boule bleue (1 bleue, 1 jaune)</text>
    <text x="8" y="66" font-size="12.5" font-weight="700" fill="#1D9E75">B. Une boule bleue (urne toute bleue)</text>
    <line x1="30" y1="110" x2="330" y2="110" stroke="#334155" stroke-width="2.5"/>
    <line x1="30" y1="102" x2="30" y2="118" stroke="#334155" stroke-width="2"/>
    <line x1="180" y1="102" x2="180" y2="118" stroke="#334155" stroke-width="2"/>
    <line x1="330" y1="102" x2="330" y2="118" stroke="#334155" stroke-width="2"/>
    <text x="30" y="136" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">0</text>
    <text x="180" y="136" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">0,5</text>
    <text x="330" y="136" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">1</text>
    <text x="30" y="88" font-size="12" font-weight="600" fill="#475569" text-anchor="start">impossible</text>
    <text x="330" y="88" font-size="12" font-weight="600" fill="#475569" text-anchor="end">certain</text>
  </g>

  <!-- ── A se place sur 0,5 (étape 2) ────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1081b-pop">
      <circle cx="180" cy="110" r="5.5" fill="#1E3A8A" stroke="#ffffff" stroke-width="1.5"/>
      <text x="180" y="98" font-size="12.5" font-weight="800" fill="#1E3A8A" text-anchor="middle">A</text>
    </g>
  </g>

  <!-- ── B se place sur 1, tout à droite (étape 3) ───────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1081b-pop">
      <circle cx="330" cy="110" r="5.5" fill="#1D9E75" stroke="#ffffff" stroke-width="1.5"/>
      <text x="318" y="98" font-size="12.5" font-weight="800" fill="#1D9E75" text-anchor="middle">B</text>
      <rect x="210" y="152" width="140" height="24" rx="8" fill="#ecfdf5" stroke="#1D9E75"/>
      <text x="280" y="168" font-size="11.5" font-weight="800" fill="#1D9E75" text-anchor="middle">B est le plus probable</text>
      <rect x="20" y="190" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="207" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Le plus à droite est le plus probable.</text>
    </g>
  </g>

  <text x="180" y="233" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_POSITION = {
    pages: [
        {
            titre: 'Les trois repères',
            lignes: [
                '**Compare l\'évènement aux trois repères** : impossible, c\'est 0. Certain, c\'est 1. Une chance sur deux, c\'est 0,5.',
                '**Les extrémités sont réservées** : très peu probable n\'est pas impossible (très près de 0, jamais à 0) ; très probable n\'est pas certain (très près de 1, jamais à 1).',
                'Pas besoin de calculer : demande-toi seulement de quel repère l\'évènement se rapproche.',
            ],
        },
        { titre: 'Un exemple : trois points sur l\'échelle', svg: SVG_TROIS_REPERES },
    ],
};

const CP_1081 = {
    position_qcm_unique: AIDE_POSITION,
    echelle_classer: AIDE_POSITION,
    comparer_qcm_unique: {
        pages: [
            {
                titre: 'Comparer sans calculer',
                lignes: [
                    '**Place les deux évènements en pensée sur l\'échelle** : le plus probable est celui qui est le plus à droite.',
                    '**Les extrémités gagnent toujours** : le certain bat même le presque certain, et tout ce qui est possible bat l\'impossible.',
                    'Deux évènements à **une chance sur deux** sont aussi probables, même avec des objets différents (une pièce, une urne...).',
                ],
            },
            { titre: 'Un exemple : deux urnes', svg: SVG_COMPARER },
        ],
    },
    // extremites_tableau_vf : volontairement ABSENT (pur rappel, cf. en-tête).
};

export default CP_1081;
